import { useEffect, useState } from "react";
import { MapPin, ChevronDown, Loader2, Crosshair } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { popularAreas } from "@/data/nigeriaStates";
import { countries, DEFAULT_COUNTRY_ID } from "@/data/countries";
import { requestBrowserLocation } from "@/lib/geolocation";
import { toast } from "sonner";
import { useUserLocation } from "@/hooks/use-user-location";

const LOCATION_LABEL_KEY = "taskratel:location-label";

export function getStoredLocationLabel(): string {
  return localStorage.getItem(LOCATION_LABEL_KEY) || "Nigeria";
}

export function setStoredLocationLabel(label: string) {
  localStorage.setItem(LOCATION_LABEL_KEY, label);
  window.dispatchEvent(new Event("taskratel:location-updated"));
}

export function MobileLocationBar() {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState(getStoredLocationLabel());
  const [locating, setLocating] = useState(false);
  const [countryId, setCountryId] = useState<string>(DEFAULT_COUNTRY_ID);
  const { label: detectedLabel, isDetecting } = useUserLocation();

  // Reflect the app-wide auto-detected location
  useEffect(() => {
    if (detectedLabel) setLabel(detectedLabel);
  }, [detectedLabel]);

  useEffect(() => {
    const handler = () => setLabel(getStoredLocationLabel());
    window.addEventListener("taskratel:location-updated", handler);
    return () => window.removeEventListener("taskratel:location-updated", handler);
  }, []);

  const pick = (name: string) => {
    setStoredLocationLabel(name);
    setLabel(name);
    setOpen(false);
  };

  const useGPS = async () => {
    setLocating(true);
    try {
      await requestBrowserLocation();
      pick("Near me");
      toast.success("Using your current location");
    } catch {
      toast.error("Couldn't access location");
    } finally {
      setLocating(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className="flex w-full items-center gap-2 border-b border-border bg-card px-4 py-2.5 text-left">
          <MapPin className="h-4 w-4 text-primary" />
          {isDetecting && !detectedLabel ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : null}
          <div className="flex-1">
            <div className="text-sm font-semibold text-foreground">{label}</div>
          </div>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </button>
      </SheetTrigger>
      <SheetContent side="bottom" className="h-[80vh] p-0">
        <SheetHeader className="border-b border-border p-4">
          <SheetTitle>Choose your location</SheetTitle>
        </SheetHeader>
        <div className="p-4">
          <Button onClick={useGPS} disabled={locating} className="w-full" variant="outline">
            {locating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Crosshair className="mr-2 h-4 w-4" />}
            Use my current location
          </Button>
        </div>
        <ScrollArea className="h-[calc(80vh-140px)]">
          <div className="px-4 pb-8">
            <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Country</div>
            <div className="mb-6 flex flex-wrap gap-2">
              {countries.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCountryId(c.id)}
                  className={`rounded-full border px-3 py-1.5 text-sm ${
                    countryId === c.id
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-background"
                  }`}
                >
                  {c.flag} {c.name}
                </button>
              ))}
            </div>

            {countryId === DEFAULT_COUNTRY_ID && (
              <>
            <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Popular areas</div>
            <div className="mb-6 grid grid-cols-2 gap-2">
              {popularAreas.slice(0, 12).map((a) => (
                <button
                  key={a.id}
                  onClick={() => pick(`${a.name}, ${a.stateLabel}`)}
                  className="rounded-lg border border-border bg-background px-3 py-2 text-left text-sm hover:border-primary"
                >
                  <div className="font-medium">{a.name}</div>
                  <div className="text-xs text-muted-foreground">{a.stateLabel}</div>
                </button>
              ))}
            </div>
              </>
            )}
            <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
              All regions
            </div>
            <div className="grid grid-cols-2 gap-2">
              {(countries.find((c) => c.id === countryId)?.states ?? []).map((s) => (
                <button
                  key={s.id}
                  onClick={() => pick(s.name)}
                  className="rounded-lg border border-border bg-background px-3 py-2 text-left text-sm hover:border-primary"
                >
                  {s.name}
                </button>
              ))}
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}